import { sortCatalogTerms } from "~/lib/services-catalog";

/**
 * WordPress manual order for the booking flow.
 *
 * The booking API (Tekio) returns services and availabilities in its own
 * order, and the wizard used to render them as they came: WordPress was only
 * looked up by code, so the order set in WP admin never reached the cards.
 * These helpers re-sort a list the API returned by the same rules `/servizi`
 * uses (`buildServiceCatalog`), so both places show one sequence:
 *
 *  - services by the `menu_order` of their `servizio` post ("Post Types
 *    Order"), merged into `servicesData` by the root loader; all-or-nothing,
 *    so a KV entry cached without the field keeps the payload order instead of
 *    a half-sorted list;
 *  - codes of one service by their position in the ACF `codes` repeater;
 *  - categories by `sortCatalogTerms`, i.e. the order of the REST array the
 *    taxonomy-order plugin writes.
 *
 * Items WordPress doesn't know keep their API order, after the known ones.
 * `sort` is stable, so equal ranks never swap.
 */

type CodeOf<T> = (item: T) => string | null | undefined;

const UNKNOWN: [number, number] = [Number.MAX_SAFE_INTEGER, 0];

function menuOrder(service: any): number | null {
  const value = service?.menu_order;
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/** [service position, code position] for every code WordPress lists. */
export function wpCodeRanks(
  servicesData: any[] | null | undefined
): Map<string, [number, number]> {
  const services = Array.isArray(servicesData) ? servicesData : [];
  const everyServiceHasOrder =
    services.length > 0 && services.every((s) => menuOrder(s) !== null);
  const position = (service: any, index: number) =>
    everyServiceHasOrder ? menuOrder(service)! : index;

  const ranks = new Map<string, [number, number]>();
  services.forEach((service, serviceIndex) => {
    const codes = Array.isArray(service?.acf?.codes) ? service.acf.codes : [];
    codes.forEach((entry: any, codeIndex: number) => {
      const code = String(entry?.code ?? "").trim();
      // First match wins, like `getCmsServiceFromList`.
      if (code && !ranks.has(code)) {
        ranks.set(code, [position(service, serviceIndex), codeIndex]);
      }
    });
  });
  return ranks;
}

function compareRanks(a: [number, number], b: [number, number]): number {
  return a[0] - b[0] || a[1] - b[1];
}

/**
 * Sorts booking-API items (services, availabilities, cards) by the WordPress
 * order of their codes. An item with several codes (a card grouping the
 * variants of one service) ranks by its first code in that order.
 */
export function sortByWpServiceOrder<T>(
  items: T[],
  codesOf: (item: T) => Array<string | null | undefined>,
  servicesData: any[] | null | undefined
): T[] {
  const ranks = wpCodeRanks(servicesData);
  const rankOf = (item: T): [number, number] =>
    codesOf(item)
      .map((code) => (code ? ranks.get(code) : undefined) ?? UNKNOWN)
      .reduce((best, rank) => (compareRanks(rank, best) < 0 ? rank : best), UNKNOWN);
  const ranked = items.map((item) => ({ item, rank: rankOf(item) }));
  return ranked
    .sort((a, b) => compareRanks(a.rank, b.rank))
    .map((entry) => entry.item);
}

/** Sorts items by the WordPress order of the category whose `acf.code` they carry. */
export function sortByWpCategoryOrder<T>(
  items: T[],
  codeOf: CodeOf<T>,
  categories: any[] | null | undefined
): T[] {
  const ordered = sortCatalogTerms(Array.isArray(categories) ? categories : []);
  const rank = new Map<string, number>();
  ordered.forEach((term: any, index: number) => {
    const code = String(term?.acf?.code ?? "").trim();
    if (code && !rank.has(code)) rank.set(code, index);
  });
  const rankOf = (item: T) => {
    const code = codeOf(item);
    return (code ? rank.get(code) : undefined) ?? Number.MAX_SAFE_INTEGER;
  };
  return items
    .map((item) => ({ item, rank: rankOf(item) }))
    .sort((a, b) => a.rank - b.rank)
    .map((entry) => entry.item);
}
